import React, { useEffect, useState } from 'react';
import { Music } from 'lucide-react';
import { getPartners, getTermsConditions } from '../services/api';
import { FloatingDots } from './FloatingDots';
import { useIsMobile } from '../hooks/useIsMobile';

export const Footer = () => {
  const [partners, setPartners] = useState([]);
  const [terms, setTerms] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partnersRes, termsRes] = await Promise.all([
        getPartners(),
        getTermsConditions()
      ]);
      setPartners(partnersRes.data);
      // API returns an array, get the first active item
      const termsData = Array.isArray(termsRes.data) ? termsRes.data[0] : termsRes.data;
      setTerms(termsData);
    } catch (error) {
      console.error('Failed to fetch footer data:', error);
    }
  };

  return (
    <footer className="bg-black border-t border-orange-500/30 py-12 relative overflow-hidden z-50">
      {/* Floating Dots Animation - Highly reduced for footer */}
      <div className="absolute inset-0 overflow-hidden">
        <FloatingDots count={isMobile ? 20 : 80} primaryColor="orange" secondaryColor="green" />
      </div>
      
      <div className="container mx-auto px-4 relative z-30">
        {/* Partners Section */}
        {partners.length > 0 && (
          <div className="mb-12">
            <h3 className="text-center text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400 mb-6 hover:scale-105 transition-transform duration-300 inline-block cursor-default leading-tight pb-1">
              Partners
            </h3>
            {/* Desktop: Centered flex-wrap, Mobile: Horizontal scroll */}
            <div className="hidden md:flex flex-wrap justify-center items-center gap-8">
              {partners.map((partner) => (
                <a
                  key={partner.id}
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative flex items-center justify-center"
                >
                  <div className="relative w-32 h-32 rounded-xl overflow-hidden bg-white/5 backdrop-blur-sm border border-orange-500/20 hover:border-orange-500/60 hover:shadow-xl hover:shadow-orange-500/30 hover:scale-110 transition-all duration-300">
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="w-full h-full object-contain p-4"
                    />
                  </div>
                </a>
              ))}
            </div>
            {/* Mobile: Horizontal scrollable */}
            <div className="md:hidden overflow-x-auto scrollbar-hide">
              <div className="flex gap-6 px-4 pb-2" style={{ minWidth: 'min-content' }}>
                {partners.map((partner) => (
                  <a
                    key={partner.id}
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative flex items-center justify-center flex-shrink-0"
                  >
                    <div className="relative w-24 h-24 rounded-xl overflow-hidden bg-white/5 backdrop-blur-sm border border-orange-500/20 active:border-orange-500/60 active:shadow-lg active:shadow-orange-500/30 transition-all duration-300">
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        className="w-full h-full object-contain p-4"
                      />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-orange-500/20 pt-8">
          {/* Copyright Text - Centered */}
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Music className="text-orange-400" />
            <span className="text-gray-400 hover:text-gray-300 transition-colors duration-300">
              © 2024 DharmaR Records. All rights reserved.
            </span>
          </div>
          
          {/* Terms & Conditions - Centered with Logos */}
          {terms && terms.url && (
            <div className="flex justify-center items-center gap-4">
              {/* Left Logo */}
              <div className="relative w-8 h-8 md:w-10 md:h-10 hover:scale-110 transition-transform duration-300">
                <img
                  src="https://customer-assets.emergentagent.com/job_dharmar-beats/artifacts/sasrtdk3_avatar%20youtube.png"
                  alt="DharmaR Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* Terms Link */}
              <a 
                href={terms.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-orange-400 transition-colors duration-300 underline"
              >
                Terms & Conditions
              </a>
              
              {/* Right Logo */}
              <div className="relative w-8 h-8 md:w-10 md:h-10 hover:scale-110 transition-transform duration-300">
                <img
                  src="https://customer-assets.emergentagent.com/job_dharmar-beats/artifacts/sasrtdk3_avatar%20youtube.png"
                  alt="DharmaR Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};